// ============================================================
// SEPTOMINO ENUMERATION
// All 108 free heptominoes (7-cell polyominoes), distinct up to
// rotation and reflection. Generated algorithmically at module
// load. Used for ore vein shapes in terrain generation.
// ============================================================

/**
 * Normalize a set of cells so min x=0, min y=0, sorted lexicographically.
 */
function normalize(cells: number[][]): number[][] {
    const minX = Math.min(...cells.map(c => c[0]));
    const minY = Math.min(...cells.map(c => c[1]));
    const shifted = cells.map(c => [c[0] - minX, c[1] - minY]);
    shifted.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    return shifted;
}

function toKey(cells: number[][]): string {
    return cells.map(c => `${c[0]},${c[1]}`).join(';');
}

/**
 * Generate all 8 symmetry transformations (4 rotations × 2 reflections).
 */
function symmetries(cells: number[][]): number[][][] {
    const result: number[][][] = [];
    let cur = cells;
    for (let r = 0; r < 4; r++) {
        result.push(normalize(cur));
        result.push(normalize(cur.map(c => [-c[0], c[1]])));
        cur = cur.map(c => [-c[1], c[0]]); // rotate 90° CW
    }
    return result;
}

/**
 * Canonical form: lexicographically smallest key among all 8 symmetries.
 */
function canonicalize(cells: number[][]): string {
    const forms = symmetries(cells);
    const keys = forms.map(toKey);
    keys.sort();
    return keys[0];
}

/**
 * Recursively enumerate all free polyominoes of size n.
 */
function enumerateFreePolyominoes(n: number): number[][][] {
    if (n === 1) return [[[0, 0]]];

    const smaller = enumerateFreePolyominoes(n - 1);
    const seen = new Set<string>();
    const result: number[][][] = [];

    for (const poly of smaller) {
        const occupied = new Set(poly.map(c => `${c[0]},${c[1]}`));
        for (const cell of poly) {
            for (const [dx, dy] of [[0, 1], [0, -1], [1, 0], [-1, 0]]) {
                const nx = cell[0] + dx;
                const ny = cell[1] + dy;
                if (occupied.has(`${nx},${ny}`)) continue;
                const newPoly = [...poly, [nx, ny]];
                const canon = canonicalize(newPoly);
                if (!seen.has(canon)) {
                    seen.add(canon);
                    result.push(normalize(newPoly));
                }
            }
        }
    }

    return result;
}

/**
 * Center a shape around its centroid (rounded) so it's roughly
 * symmetric around (0,0) — the ore seed point.
 */
function centerShape(shape: number[][]): number[][] {
    const cx = Math.round(shape.reduce((s, c) => s + c[0], 0) / shape.length);
    const cy = Math.round(shape.reduce((s, c) => s + c[1], 0) / shape.length);
    return shape.map(c => [c[0] - cx, c[1] - cy]);
}

/** All 108 free heptominoes, centered around (0,0). */
export const ALL_SEPTOMINOES: number[][][] = enumerateFreePolyominoes(7).map(centerShape);

/** Maximum extent of any septomino cell from center. */
export const SEPTOMINO_MAX_EXTENT = Math.max(
    ...ALL_SEPTOMINOES.flatMap(s => s.flatMap(c => [Math.abs(c[0]), Math.abs(c[1])]))
);

/**
 * Deterministic hash for picking a septomino shape from world coordinates.
 */
function hashPosition(seed: number, x: number, y: number): number {
    let h = (seed ^ (x * 374761393 + y * 668265263)) >>> 0;
    h = Math.imul(h, 2654435761) >>> 0;
    h = (h ^ (h >>> 16)) >>> 0;
    return h;
}

/**
 * Get the septomino shape for an ore seed point at (x, y).
 * Returns 7 [dx, dy] offsets from the seed point, uniformly chosen
 * from all 108 free heptominoes.
 */
export function getOreSeptomino(seed: number, x: number, y: number): number[][] {
    const h = hashPosition(seed, x, y);
    const index = h % ALL_SEPTOMINOES.length;
    return ALL_SEPTOMINOES[index];
}
